import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isRegister, setIsRegister] = useState(false);
  const [isChangePassword, setIsChangePassword] = useState(false);
  const [isChangePasswordForm, setIsChangePasswordForm] = useState(false);
  const [name, setName] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { login, register, user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isRegister) {
        await register(email, password, name);
        toast.success('Account created successfully!');
      } else {
        await login(email, password);
        toast.success('Logged in successfully!');
      }
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsChangePassword(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // Handle change password when not logged in (uses email verification)
  const handleChangePasswordWithEmail = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password-with-email`, {
        email: email,
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success('Password changed successfully! Please sign in with your new password.');
      setEmail('');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setIsChangePasswordForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  // If user is logged in and wants to change password
  if (user && isChangePassword) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="h-16 w-full bg-[#5ce1e6]" />
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <button 
              onClick={() => setIsChangePassword(false)} 
              className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0e7490] mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden">
                  <img src="/images/Logo4.png" alt="BN Physiotherapy" className="w-full h-full object-cover" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Change Password
                </h1>
                <p className="text-slate-500 text-sm mt-1">
                  Update your account password
                </p>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label>Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                      autoComplete="off"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label>New Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    data-testid="new-password-input"
                  />
                </div>

                <div>
                  <Label>Confirm New Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#5ce1e6] text-slate-900 hover:bg-[#22d3ee]"
                  disabled={loading}
                  data-testid="change-password-btn"
                >
                  {loading ? 'Please wait...' : 'Change Password'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If user is logged in, show options
  if (user) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="h-16 w-full bg-[#5ce1e6]" />
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0e7490] mb-8">
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden">
                  <img src="/images/Logo4.png" alt="BN Physiotherapy" className="w-full h-full object-cover" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Welcome, {user.name}
                </h1>
                <p className="text-slate-500 text-sm mt-1">
                  {user.email}
                </p>
              </div>

              <div className="space-y-3">
                <Button 
                  onClick={() => navigate('/admin')}
                  className="w-full bg-[#5ce1e6] text-slate-900 hover:bg-[#22d3ee]"
                  data-testid="go-to-dashboard-btn"
                >
                  Go to Dashboard
                </Button>
                
                <Button 
                  onClick={() => setIsChangePassword(true)}
                  variant="outline"
                  className="w-full"
                  data-testid="change-password-link"
                >
                  Change Password
                </Button>
                
                <Button 
                  onClick={() => {
                    logout();
                    toast.success('Logged out successfully');
                  }}
                  variant="ghost"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="logout-btn"
                >
                  Logout
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Change password form (when not logged in)
  if (isChangePasswordForm) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="h-16 w-full bg-[#5ce1e6]" />
        
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <button 
              onClick={() => {
                setIsChangePasswordForm(false);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
              }} 
              className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0e7490] mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Login
            </button>

            <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden">
                  <img src="/images/Logo4.png" alt="BN Physiotherapy" className="w-full h-full object-cover" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Change Password
                </h1>
                <p className="text-slate-500 text-sm mt-1">
                  Update your account password
                </p>
              </div>

              <form onSubmit={handleChangePasswordWithEmail} className="space-y-4">
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    autoComplete="off"
                    data-testid="change-pw-email-input"
                  />
                </div>

                <div>
                  <Label>Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                      required
                      autoComplete="off"
                      data-testid="change-pw-current-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label>New Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    autoComplete="new-password"
                    data-testid="change-pw-new-input"
                  />
                </div>

                <div>
                  <Label>Confirm New Password</Label>
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    autoComplete="new-password"
                    data-testid="change-pw-confirm-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-[#5ce1e6] text-slate-900 hover:bg-[#22d3ee]"
                  disabled={loading}
                  data-testid="change-pw-submit-btn"
                >
                  {loading ? 'Please wait...' : 'Change Password'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Turquoise Header */}
      <div className="h-16 w-full bg-[#5ce1e6]" />
      
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0e7490] mb-8">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100">
            <div className="text-center mb-8">
              <div className="w-20 h-20 mx-auto mb-4 rounded-full overflow-hidden">
                <img src="/images/Logo4.png" alt="BN Physiotherapy" className="w-full h-full object-cover" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {isRegister ? 'Create Account' : 'Admin Login'}
              </h1>
              <p className="text-slate-500 text-sm mt-1">
                {isRegister ? 'Register for admin access' : 'Sign in to manage appointments'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
              {isRegister && (
                <div>
                  <Label>Name</Label>
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required={isRegister}
                    autoComplete="off"
                    data-testid="register-name-input"
                  />
                </div>
              )}
              
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  autoComplete="off"
                  data-testid="login-email-input"
                />
              </div>

              <div>
                <Label>Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    autoComplete="new-password"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setIsChangePasswordForm(true)}
                  className="text-sm text-[#0e7490] hover:underline mt-2"
                  data-testid="change-password-link-login"
                >
                  Change Password
                </button>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#5ce1e6] text-slate-900 hover:bg-[#22d3ee]"
                disabled={loading}
                data-testid="login-submit-btn"
              >
                {loading ? 'Please wait...' : (isRegister ? 'Create Account' : 'Sign In')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="text-sm text-[#0e7490] hover:underline"
                data-testid="toggle-auth-mode"
              >
                {isRegister ? 'Already have an account? Sign in' : 'Need an account? Register'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
